import { S, announce, getSnapshot } from '../../app/state';
import {
  LEFT_CONTROLS,
  LEFT_PRIORITY,
  RIGHT_CONTROLS,
  RIGHT_PRIORITY,
  type TopbarControl,
} from './topbarControls';

/**
 * THE TOP BAR'S FIT CONTROLLER (plan 1.3) — the one measuring mechanism, replacing the old
 * `fitTopbar()` whose only answer was to hide the search.
 *
 * The 1180px/720px CSS media queries still do the cheap part (dropping button labels). This
 * does the part CSS cannot: when the side zones still leave the centre less than a usable
 * search width, controls MOVE into a per-side "…" menu instead of the search vanishing.
 * Hiding `.tb-center` survives only as the last-resort fallback, for windows so narrow
 * (~<480px) that even a fully-overflowed bar cannot spare the minimum.
 *
 * ── HOW IT MEASURES ──────────────────────────────────────────────────────────────────────
 * rAF-batched: `requestTopbarFit()` coalesces however many notifications arrive in a frame —
 * the zones' layout effects, the ResizeObserver on `.topbar`, boot — into one measure pass.
 * The pass reads real DOM widths for every control that is inline right now (updating a
 * per-control cache), and uses the cached width for controls currently in a menu. That is the
 * reveal-then-measure contract: every control starts inline (the overflow set is empty until
 * this writes it), so by the time one is pulled its width is known. A cached width can go
 * stale across the 1180px label boundary; the worst case is one extra pass — the control
 * comes back, is re-measured for real, and the next pass settles — never a loop, because the
 * cache converges on the truth.
 *
 * The zones' FIXED parts (brand, edit switch, presence, chip, logout, search) are never
 * enumerated: each side's fixed width is reconstructed as "the zone's width right now, minus
 * its inline registry controls, minus its menu button". Flex-gap arithmetic makes a child
 * cost exactly `width + gap`, so the subtraction is exact whatever the mix.
 *
 * ── WHO GIVES FIRST ──────────────────────────────────────────────────────────────────────
 * Per side, the priority lists in `topbarControls.tsx`. ACROSS sides, the right gives first
 * and the left only when the right is exhausted: the left buttons exist only in edit mode and
 * are the tools of the mode the user is actively working in, while the right is toggles and
 * pages that lose nothing by living one click deeper. This is also what keeps the default
 * 1440px edit-mode bar holding every edit button inline, which the untouched half of
 * `e2e/22-topbar-left.spec.ts` asserts.
 *
 * ── THE FEEDBACK-LOOP GUARD IS LOAD-BEARING ──────────────────────────────────────────────
 * Writing `S.topbarOverflow` re-renders the zones, whose layout effects notify this
 * controller again. The pass therefore commits ONLY when the computed set differs from the
 * current one — that is what turns the cycle into "one extra no-op pass" instead of an
 * infinite render loop (and it is also what makes StrictMode's double effects harmless).
 */

const ALL_CONTROLS: readonly TopbarControl[] = [...RIGHT_CONTROLS, ...LEFT_CONTROLS];

/** The minimum room the centre zone must keep — the old fitTopbar()'s own threshold. */
const CENTER_MIN = 150;
/** An icon-only `.btn` (theme, help, logout all measure 42px) — the fallback before a menu
 *  button has ever been measured, and for a control that somehow has no cached width. */
const BTN_FALLBACK = 42;

type Side = 'left' | 'right';

/** Natural width of each control, refreshed whenever it is inline. Cleared never — a stale
 *  entry is self-correcting (see the header). */
const widthCache = new Map<string, number>();
/** The "…" buttons' own widths, measured once each exists. */
const menuBtnW: Record<Side, number> = { left: BTN_FALLBACK, right: BTN_FALLBACK };

let scheduled = false;

/**
 * Ask for a measure pass. Safe to call from anywhere, any number of times per frame.
 *
 * This is what the action table and the page scope publish under the name `fitTopbar`:
 * the name ~5 call sites and one spec already use, pointing at the new mechanism.
 */
export function requestTopbarFit(): void {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    measure();
  });
}

let observer: ResizeObserver | null = null;

/**
 * Watch the bar itself. A window resize is just one way the bar's box changes; observing the
 * element covers them all, and replaces the old window `resize` listener in `chrome.ts`.
 */
export function initTopbarFit(): void {
  if (observer) return;
  const tb = document.querySelector('.topbar');
  if (!tb) return;
  observer = new ResizeObserver(() => requestTopbarFit());
  observer.observe(tb);
}

function measure(): void {
  const tb = document.querySelector('.topbar') as HTMLElement | null;
  const L = document.querySelector('.tb-left') as HTMLElement | null;
  const R = document.querySelector('.tb-right') as HTMLElement | null;
  const C = document.querySelector('.tb-center') as HTMLElement | null;
  if (!tb || !L || !R || !C) return;
  const s = getSnapshot();

  const cs = getComputedStyle(tb);
  const pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
  const zoneGap = parseFloat(cs.columnGap) || parseFloat(cs.gap) || 12;
  const gapOf: Record<Side, number> = {
    left: parseFloat(getComputedStyle(L).columnGap) || 14,
    right: parseFloat(getComputedStyle(R).columnGap) || 10,
  };

  // 1. Refresh the cache from whatever is inline right now.
  const inlineNow = new Set<string>();
  for (const c of ALL_CONTROLS) {
    const el = document.getElementById(c.domId);
    if (el && el.offsetWidth > 0) {
      widthCache.set(c.id, el.offsetWidth);
      inlineNow.add(c.id);
    }
  }
  const menuEls: Record<Side, HTMLElement | null> = {
    left: document.getElementById('tb-ovf-left'),
    right: document.getElementById('tb-ovf-right'),
  };
  for (const side of ['left', 'right'] as const) {
    const el = menuEls[side];
    if (el && el.offsetWidth > 0) menuBtnW[side] = el.offsetWidth;
  }

  // 2. Reconstruct each side's fixed width: the zone minus its overflowable children.
  const fixed: Record<Side, number> = { left: L.offsetWidth, right: R.offsetWidth };
  for (const c of ALL_CONTROLS) {
    if (inlineNow.has(c.id)) fixed[c.side] -= (widthCache.get(c.id) ?? 0) + gapOf[c.side];
  }
  for (const side of ['left', 'right'] as const) {
    const el = menuEls[side];
    if (el && el.offsetWidth > 0) fixed[side] -= el.offsetWidth + gapOf[side];
  }

  // 3. What the bar would need with everything inline.
  const visible = ALL_CONTROLS.filter((c) => c.visible(s));
  const costOf = (c: TopbarControl) => (widthCache.get(c.id) ?? BTN_FALLBACK) + gapOf[c.side];
  const width: Record<Side, number> = { left: fixed.left, right: fixed.right };
  for (const c of visible) width[c.side] += costOf(c);

  // 4. Pull until the centre keeps its minimum — right side first, then left.
  const byId = new Map(visible.map((c) => [c.id, c]));
  const pullOrder: TopbarControl[] = [...RIGHT_PRIORITY, ...LEFT_PRIORITY]
    .map((id) => byId.get(id))
    .filter((c): c is TopbarControl => !!c);
  const budget = tb.clientWidth - pad - 2 * zoneGap - CENTER_MIN;

  const out: string[] = [];
  const pulledFrom: Record<Side, boolean> = { left: false, right: false };
  for (const c of pullOrder) {
    if (width.left + width.right <= budget) break;
    width[c.side] -= costOf(c);
    if (!pulledFrom[c.side]) {
      pulledFrom[c.side] = true;
      width[c.side] += menuBtnW[c.side] + gapOf[c.side]; // the "…" button takes a seat too
    }
    out.push(c.id);
  }

  // 5. Commit — only when the answer changed (the guard the header calls load-bearing).
  const prev = S.topbarOverflow;
  if (out.length !== prev.length || out.some((id, i) => id !== prev[i])) {
    S.topbarOverflow = out;
    announce();
  }

  // 6. Last resort: even a fully-overflowed bar leaves the centre under its minimum.
  C.style.display = width.left + width.right > budget ? 'none' : '';
}
